// Authentication service for Kaya application
// This service handles all authentication methods including OAuth integrations

// Configuration - these would typically come from environment variables
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://kaya-servers.onrender.com/api';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'YOUR_GOOGLE_CLIENT_ID';
const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || 'YOUR_GITHUB_CLIENT_ID';

// Determine if we're in production or development (localhost)
// For OAuth callbacks, we always use the backend server in production
const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
const FRONTEND_BASE_URL = isProduction ? 'https://kaya-servers.onrender.com' : 'http://localhost:3000';

class AuthService {
  private static instance: AuthService;
  private token: string | null = null;
  private user: any = null;
  private tokenExpiry: number | null = null;

  private constructor() {
    // Check for existing session on initialization
    this.loadStoredSession();
  }

  static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  // Load stored session from localStorage
  private loadStoredSession(): void {
    try {
      const storedToken = localStorage.getItem('kaya_auth_token');
      const storedUser = localStorage.getItem('kaya_user');
      const storedExpiry = localStorage.getItem('kaya_token_expiry');
      
      if (storedToken && storedExpiry) {
        const expiry = parseInt(storedExpiry, 10);
        if (expiry > Date.now()) {
          this.token = storedToken;
          this.tokenExpiry = expiry;
          if (storedUser) {
            // Parse the stored user data
            this.user = JSON.parse(storedUser);
          }
        } else {
          // Token expired, clear storage
          this.clearStoredSession();
        }
      }
    } catch (error) {
      console.error('Error loading stored session:', error);
      this.clearStoredSession();
    }
  }

  // Store session in localStorage
  private storeSession(): void {
    try {
      if (this.token) {
        localStorage.setItem('kaya_auth_token', this.token);
        localStorage.setItem('kaya_token_expiry', this.tokenExpiry?.toString() || '');
        if (this.user) {
          localStorage.setItem('kaya_user', JSON.stringify(this.user));
        }
      }
    } catch (error) {
      console.error('Error storing session:', error);
    }
  }

  // Clear stored session
  private clearStoredSession(): void {
    localStorage.removeItem('kaya_auth_token');
    localStorage.removeItem('kaya_user');
    localStorage.removeItem('kaya_token_expiry');
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    if (!this.token || !this.tokenExpiry) return false;
    return this.tokenExpiry > Date.now();
  }

  // Get current auth token
  getToken(): string | null {
    return this.isAuthenticated() ? this.token : null;
  }

  // Get user info
  getUser(): any {
    return this.user;
  }

  // Update user info
  updateUser(userData: any): void {
    if (this.user) {
      this.user = { ...this.user, ...userData };
      this.storeSession();
    }
  }

  // Make authenticated API request
  private async apiRequest(endpoint: string, options: RequestInit = {}): Promise<any> {
    const token = this.getToken();
    
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'API request failed');
    }
    
    return data;
  }

  // Traditional email/password login
  async login(email: string, password: string): Promise<{ success: boolean; token?: string; user?: any; error?: string }> {
    try {
      const response = await this.apiRequest('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      if (response.success && response.token) {
        this.token = response.token;
        this.user = response.user;
        this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
        this.storeSession();
        return { success: true, token: this.token as string, user: this.user };
      } else {
        return { success: false, error: response.error || 'Login failed' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Login error' };
    }
  }

  // User registration
  async signup(firstName: string, lastName: string, email: string, password: string): Promise<{ success: boolean; token?: string; user?: any; error?: string }> {
    try {
      const response = await this.apiRequest('/auth/signup', {
        method: 'POST',
        body: JSON.stringify({ firstName, lastName, email, password }),
      });

      if (response.success && response.token) {
        this.token = response.token;
        this.user = response.user;
        this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
        this.storeSession();
        return { success: true, token: this.token as string, user: this.user };
      } else {
        return { success: false, error: response.error || 'Signup failed' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Signup error' };
    }
  }

  // Google OAuth login
  async loginWithGoogle(): Promise<{ success: boolean; token?: string; user?: any; error?: string }> {
    try {
      // For web flow, we redirect to Google OAuth
      if (typeof window !== 'undefined') {
        // ✅ FIX: Use the correct redirect URI based on environment
        // Ensure this matches exactly what's registered in Google Cloud Console
        const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
        const redirectUri = isProduction 
          ? 'https://kaya-servers.onrender.com/api/auth/callback/google'
          : 'http://localhost:3000/api/auth/callback/google';
        
        const encodedRedirectUri = encodeURIComponent(redirectUri);
        const googleOAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${GOOGLE_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=email profile&access_type=offline`;
        
        // Redirect to Google OAuth
        window.location.href = googleOAuthUrl;
        return { success: true };
      }
      
      // For server-side or non-browser environments
      return { success: false, error: 'Google OAuth only available in browser environment' };
    } catch (error: any) {
      console.error('Google OAuth error:', error);
      return { success: false, error: error.message || 'Google login error' };
    }
  }

  // GitHub OAuth login
  async loginWithGitHub(): Promise<{ success: boolean; token?: string; user?: any; error?: string }> {
    try {
      // For web flow, we redirect to GitHub OAuth
      if (typeof window !== 'undefined') {
        // ✅ FIX: Use the correct redirect URI based on environment
        const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';
        const redirectUri = isProduction 
          ? 'https://kaya-servers.onrender.com/api/auth/callback/github'
          : 'http://localhost:3000/api/auth/callback/github';
        
        const encodedRedirectUri = encodeURIComponent(redirectUri);
        const githubOAuthUrl = `https://github.com/login/oauth/authorize?client_id=${GITHUB_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&scope=user:email`;
        
        // Redirect to GitHub OAuth
        window.location.href = githubOAuthUrl;
        return { success: true };
      }
      
      // For server-side or non-browser environments
      return { success: false, error: 'GitHub OAuth only available in browser environment' };
    } catch (error: any) {
      console.error('GitHub OAuth error:', error);
      return { success: false, error: error.message || 'GitHub login error' };
    }
  }

  // Handle Google OAuth callback
  async handleGoogleCallback(code: string): Promise<{ success: boolean; token?: string; user?: any; error?: string }> {
    try {
      // Validate the code parameter
      if (!code) {
        console.error('No authorization code received from Google OAuth');
        return { success: false, error: 'No authorization code received' };
      }

      console.log('Exchanging Google OAuth code for token...');
      
      // Call the backend API to exchange the code for a token using POST for security
      const response = await fetch(`${API_BASE_URL}/auth/callback/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to exchange code for token');
      }
      
      if (data.success && data.token) {
        this.token = data.token;
        this.user = data.user;
        this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
        this.storeSession();
        console.log('Google OAuth successful for user:', this.user);
        return { success: true, token: this.token as string, user: this.user };
      } else {
        console.error('Google OAuth failed:', data.error || 'Unknown error');
        return { success: false, error: data.error || 'Google authentication failed' };
      }
    } catch (error: any) {
      console.error('Google OAuth callback error:', error);
      return { success: false, error: error.message || 'Google authentication error' };
    }
  }

  // Handle GitHub OAuth callback
  async handleGitHubCallback(code: string): Promise<{ success: boolean; token?: string; user?: any; error?: string }> {
    try {
      // Validate the code parameter
      if (!code) {
        console.error('No authorization code received from GitHub OAuth');
        return { success: false, error: 'No authorization code received' };
      }

      console.log('Exchanging GitHub OAuth code for token...');
      
      // Call the backend API to exchange the code for a token using POST for security
      const response = await fetch(`${API_BASE_URL}/auth/callback/github`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to exchange code for token');
      }
      
      if (data.success && data.token) {
        this.token = data.token;
        this.user = data.user;
        this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
        this.storeSession();
        console.log('GitHub OAuth successful for user:', this.user);
        return { success: true, token: this.token as string, user: this.user };
      } else {
        console.error('GitHub OAuth failed:', data.message || 'Unknown error');
        return { success: false, error: data.message || 'GitHub authentication failed' };
      }
    } catch (error: any) {
      console.error('GitHub OAuth callback error:', error);
      return { success: false, error: error.message || 'GitHub authentication error' };
    }
  }

  // Refresh token
  async refreshToken(): Promise<boolean> {
    try {
      const response = await this.apiRequest('/auth/refresh', {
        method: 'POST',
      });

      if (response.success && response.token) {
        this.token = response.token;
        this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
        this.storeSession();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Logout
  async logout(): Promise<void> {
    try {
      // Notify backend of logout
      await this.apiRequest('/auth/logout', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Clear local session regardless of backend response
      this.token = null;
      this.user = null;
      this.tokenExpiry = null;
      this.clearStoredSession();
    }
  }

  // Handle OAuth callback from desktop app or web OAuth flow
  async handleOAuthCallback(token: string): Promise<boolean> {
    if (token) {
      try {
        // Verify token with backend
        const response = await this.apiRequest('/auth/verify', {
          method: 'POST',
          body: JSON.stringify({ token }),
        });

        if (response.success && response.user) {
          this.token = token;
          this.user = response.user;
          this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
          this.storeSession();
          return true;
        }
      } catch (error) {
        console.error('Token verification failed:', error);
      }
    }
    return false;
  }
  
  // Set token directly (for desktop app OAuth flow or auth success page)
  setToken(token: string): void {
    // Decode the JWT token to extract user info
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      this.token = token;
      this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000); // 24 hours
      // Create user object from token payload
      this.user = {
        id: payload.userId,
        email: payload.email,
        name: payload.name,
        username: payload.username
      };
      this.storeSession();
    } catch (error) {
      console.error('Error decoding token:', error);
    }
  }
}

export default AuthService.getInstance();